package it.neckar.lizergy.model.http

import it.neckar.lizergy.model.configuration.PhotovoltaicsConfiguration.PhotovoltaicsConfigurationId
import it.neckar.lizergy.model.configuration.quote.QuoteSnapshot.QuoteSnapshotId
import it.neckar.lizergy.model.project.ProjectConfiguration.PhotovoltaicsProjectId
import it.neckar.open.annotations.Effective
import it.neckar.open.http.Url
import it.neckar.open.http.UrlPattern
import it.neckar.uuid.resolve

/**
 * Storage endpoints interface
 *
 * Note: @Effective annotations on properties use relative paths that will be combined with baseUrl
 */
interface StorageEndpoints : Endpoints {

  @Effective("projects")
  val projectsUrl: UrlPattern.Relative0
    get() = baseUrl + "projects"

  @Effective("archive-projects")
  val archiveProjectsUrl: UrlPattern.Relative0
    get() = baseUrl + "archive-projects"

  @Effective("resolved-projects")
  val resolvedProjectsUrl: UrlPattern.Relative0
    get() = baseUrl + "resolved-projects"

  @Effective("resolved-projects/add-multiple-projects")
  val multipleResolvedProjectsUrl: UrlPattern.Relative0
    get() = baseUrl + "resolved-projects/add-multiple-projects"

  @Effective("projects/{projectId}")
  val projectUrl: UrlPattern.Relative1
    get() = projectsUrl + EndpointsParameters.projectId

  fun projectUrl(projectId: PhotovoltaicsProjectId): Url.Relative = projectUrl.resolve(projectId)


  @Effective("archive-projects/{projectId}")
  val archiveProjectUrl: UrlPattern.Relative1
    get() = archiveProjectsUrl + EndpointsParameters.projectId

  fun archiveProjectUrl(projectId: PhotovoltaicsProjectId): Url.Relative = archiveProjectUrl.resolve(projectId)


  @Effective("resolved-projects/{projectId}")
  val resolvedProjectUrl: UrlPattern.Relative1
    get() = resolvedProjectsUrl + EndpointsParameters.projectId

  fun resolvedProjectUrl(projectId: PhotovoltaicsProjectId): Url.Relative = resolvedProjectUrl.resolve(projectId)


  @Effective("verifications")
  val verifications: UrlPattern.Relative0
    get() = Url.pattern("verifications")

  @Effective("projects/{projectId}/verifications")
  val verificationsUrl: UrlPattern.Relative1
    get() = projectUrl + "$verifications"

  fun verificationsUrl(projectId: PhotovoltaicsProjectId): Url.Relative = verificationsUrl.resolve(projectId)

  @Effective("blueprint")
  val blueprint: UrlPattern.Relative0
    get() = Url.pattern("blueprint")

  @Effective("projects/{projectId}/blueprint")
  val blueprintUrl: UrlPattern.Relative1
    get() = projectUrl + "$blueprint"

  @Effective("orderSpecialMaterial")
  val orderSpecialMaterial: UrlPattern.Relative0
    get() = Url.pattern("orderSpecialMaterial")

  @Effective("projects/{projectId}/orderSpecialMaterial")
  val orderSpecialMaterialUrl: UrlPattern.Relative1
    get() = projectUrl + orderSpecialMaterial

  @Effective("gridAssessment")
  val gridAssessment: UrlPattern.Relative0
    get() = Url.pattern("gridAssessment")

  @Effective("projects/{projectId}/gridAssessment")
  val gridAssessmentUrl: UrlPattern.Relative1
    get() = projectUrl + gridAssessment

  @Effective("assemblyPortfolio")
  val assemblyPortfolio: UrlPattern.Relative0
    get() = Url.pattern("assemblyPortfolio")

  @Effective("projects/{projectId}/assemblyPortfolio")
  val assemblyPortfolioUrl: UrlPattern.Relative1
    get() = projectUrl + assemblyPortfolio

  @Effective("advanceInvoice")
  val advanceInvoice: UrlPattern.Relative0
    get() = Url.pattern("advanceInvoice")

  @Effective("projects/{projectId}/advanceInvoice")
  val advanceInvoiceUrl: UrlPattern.Relative1
    get() = projectUrl + advanceInvoice

  @Effective("assemblyRoof")
  val assemblyRoof: UrlPattern.Relative0
    get() = Url.pattern("assemblyRoof")

  @Effective("projects/{projectId}/assemblyRoof")
  val assemblyRoofUrl: UrlPattern.Relative1
    get() = projectUrl + assemblyRoof

  @Effective("assemblyBasement")
  val assemblyBasement: UrlPattern.Relative0
    get() = Url.pattern("assemblyBasement")

  @Effective("projects/{projectId}/assemblyBasement")
  val assemblyBasementUrl: UrlPattern.Relative1
    get() = projectUrl + assemblyBasement

  @Effective("switchMeterBox")
  val switchMeterBox: UrlPattern.Relative0
    get() = Url.pattern("switchMeterBox")

  @Effective("projects/{projectId}/switchMeterBox")
  val switchMeterBoxUrl: UrlPattern.Relative1
    get() = projectUrl + switchMeterBox

  @Effective("startupOperations")
  val startupOperations: UrlPattern.Relative0
    get() = Url.pattern("startupOperations")

  @Effective("projects/{projectId}/startupOperations")
  val startupOperationsUrl: UrlPattern.Relative1
    get() = projectUrl + startupOperations

  @Effective("finishingUp")
  val finishingUp: UrlPattern.Relative0
    get() = Url.pattern("finishingUp")

  @Effective("projects/{projectId}/finishingUp")
  val finishingUpUrl: UrlPattern.Relative1
    get() = projectUrl + finishingUp

  @Effective("finalAccount")
  val finalAccount: UrlPattern.Relative0
    get() = Url.pattern("finalAccount")

  @Effective("projects/{projectId}/finalAccount")
  val finalAccountUrl: UrlPattern.Relative1
    get() = projectUrl + finalAccount

  @Effective("documentation")
  val documentation: UrlPattern.Relative0
    get() = Url.pattern("documentation")

  @Effective("projects/{projectId}/documentation")
  val documentationUrl: UrlPattern.Relative1
    get() = projectUrl + documentation

  @Effective("projects/{projectId}/configurations")
  val configurationsUrl: UrlPattern.Relative1
    get() = projectUrl + "configurations"

  fun configurationsUrl(projectId: PhotovoltaicsProjectId): Url.Relative = configurationsUrl.resolve(projectId)

  fun configurationsForProjectUrl(projectId: PhotovoltaicsProjectId): Url.Relative = configurationsUrl.resolve(projectId)

  @Effective("resolved-projects/{projectId}/quote-configurations")
  val quoteConfigurationsUrl: UrlPattern.Relative1
    get() = resolvedProjectUrl + "quote-configurations"

  fun quoteConfigurationsForProjectUrl(projectId: PhotovoltaicsProjectId): Url.Relative = quoteConfigurationsUrl.resolve(projectId)


  @Effective("projects/{projectId}/configurations/{configurationId}")
  val configurationUrl: UrlPattern.Relative2
    get() = configurationsUrl + EndpointsParameters.configurationId

  fun configurationUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = configurationUrl.resolve(projectId, configurationId)


  @Effective("resolved-projects/{projectId}/quote-configurations/{configurationId}")
  val quoteConfigurationUrl: UrlPattern.Relative2
    get() = quoteConfigurationsUrl + EndpointsParameters.configurationId

  fun quoteConfigurationUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = quoteConfigurationUrl.resolve(projectId, configurationId)

  @Effective("module-layouts")
  val moduleLayouts: UrlPattern.Relative0
    get() = Url.pattern("module-layouts")

  @Effective("projects/{projectId}/configurations/{configurationId}/module-layouts")
  val moduleLayoutsUrl: UrlPattern.Relative2
    get() = configurationUrl.plus(moduleLayouts)

  @Effective("quote-snapshots")
  val quoteSnapshots: UrlPattern.Relative0
    get() = Url.pattern("quote-snapshots")

  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots")
  val quoteSnapshotsUrl: UrlPattern.Relative2
    get() = configurationUrl + quoteSnapshots

  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots/{quoteSnapshotId}")
  val quoteSnapshotUrl: UrlPattern.Relative3
    get() = quoteSnapshotsUrl + EndpointsParameters.quoteSnapshotId

  fun quoteSnapshotUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, snapshotId: QuoteSnapshotId): Url.Relative = quoteSnapshotUrl.resolve(projectId, configurationId, snapshotId)


  @Effective("projects/{projectId}/configurations/{configurationId}/earnings-distribution")
  val earningsDistributionUrl: UrlPattern.Relative2
    get() = configurationUrl + "earnings-distribution"

  fun earningsDistributionUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = earningsDistributionUrl.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/manual-quote-elements")
  val manualQuoteElementsUrl: UrlPattern.Relative2
    get() = configurationUrl + "manual-quote-elements"

  fun manualQuoteElementsUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = manualQuoteElementsUrl.resolve(projectId, configurationId)
}
